import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { db } from '../lib/firebase';
import { collection, query, where, getDocs, doc, updateDoc, onSnapshot, orderBy } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Footer } from '../components/Footer';
import { toast } from 'sonner';
import { 
  Calendar, 
  Clock, 
  MapPin, 
  Users, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Gamepad2,
  Trash2
} from 'lucide-react';

export default function Dashboard() {
  const { user, userData, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      // Set up real-time listener
      const q = query(
        collection(db, 'reservations'),
        where('userId', '==', user.uid),
        orderBy('createdAt', 'desc')
      );

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setReservations(data);
        setLoading(false);
      }, (error) => {
        console.error('Error fetching reservations:', error);
        setLoading(false);
      });

      return () => unsubscribe();
    }
  }, [user]);

  const handleCancel = async (reservationId) => {
    if (!window.confirm('Rezervasiyanı ləğv etmək istəyirsiniz?')) return;

    try {
      await updateDoc(doc(db, 'reservations', reservationId), {
        status: 'cancelled',
        updatedAt: new Date().toISOString()
      });
      toast.success('Rezervasiya ləğv edildi');
    } catch (error) {
      console.error('Error cancelling reservation:', error);
      toast.error('Xəta baş verdi');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'confirmed':
        return (
          <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm font-medium flex items-center gap-1">
            <CheckCircle className="w-4 h-4" /> Təsdiqlənib
          </span>
        );
      case 'pending':
        return (
          <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-sm font-medium flex items-center gap-1">
            <AlertCircle className="w-4 h-4" /> Gözləyir
          </span>
        );
      case 'cancelled':
        return (
          <span className="px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-sm font-medium flex items-center gap-1">
            <XCircle className="w-4 h-4" /> Ləğv edilib
          </span>
        );
      default:
        return null;
    }
  };

  const isUpcoming = (date, time) => {
    const reservationDateTime = new Date(`${date}T${time}`);
    return reservationDateTime > new Date();
  };

  // Separate upcoming and past reservations
  const upcomingReservations = reservations.filter(r => 
    isUpcoming(r.date, r.time) && r.status !== 'cancelled'
  );
  const pastReservations = reservations.filter(r => 
    !isUpcoming(r.date, r.time) || r.status === 'cancelled'
  );

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#050000] pt-20 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050000] pt-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
            Salam, {userData?.name || user?.email}!
          </h1>
          <p className="text-gray-400">Rezervasiyalarınızı buradan idarə edin</p>
        </div>

        {/* Upcoming Reservations */}
        <section className="mb-12">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-red-500" />
            Qarşıdakı Rezervasiyalar
          </h2>

          {upcomingReservations.length === 0 ? (
            <div className="bg-[#0F0F0F] border border-white/10 rounded-xl p-8 text-center">
              <Gamepad2 className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg text-white mb-2">Aktiv rezervasiya yoxdur</h3>
              <p className="text-gray-400 mb-4">Oyun seçib rezervasiya edin</p>
              <Button 
                className="bg-red-500 hover:bg-red-600"
                onClick={() => navigate('/games')}
              >
                Oyunlara bax
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {upcomingReservations.map((reservation, index) => (
                <motion.div
                  key={reservation.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`bg-[#0F0F0F] border rounded-xl p-6 ${
                    reservation.status === 'confirmed' 
                      ? 'border-green-500/30' 
                      : 'border-yellow-500/30'
                  }`}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    {/* Left - Game & Branch */}
                    <div className="flex items-center gap-4">
                      <div className="text-center bg-red-500/10 rounded-lg p-3 min-w-[70px]">
                        <div className="text-2xl font-bold text-red-500">
                          {reservation.date.split('-')[2]}
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(reservation.date).toLocaleDateString('az-AZ', { month: 'short' })}
                        </div>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-white">{reservation.gameName}</h3>
                        <div className="flex items-center gap-2 text-gray-400 text-sm">
                          <MapPin className="w-4 h-4" />
                          {reservation.branchName}
                        </div>
                        <div className="flex items-center gap-4 mt-1">
                          <span className="flex items-center gap-1 text-gray-400 text-sm">
                            <Clock className="w-4 h-4" />
                            {reservation.time}
                          </span>
                          <span className="flex items-center gap-1 text-gray-400 text-sm">
                            <Users className="w-4 h-4" />
                            {reservation.playerCount} nəfər
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Right - Status & Actions */}
                    <div className="flex items-center gap-4">
                      {getStatusBadge(reservation.status)}
                      
                      {reservation.status !== 'cancelled' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-500 text-red-500 hover:bg-red-500/10"
                          onClick={() => handleCancel(reservation.id)}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Ləğv Et
                        </Button>
                      )}
                    </div>
                  </div>

                  {reservation.status === 'pending' && (
                    <div className="mt-4 p-3 bg-yellow-500/10 rounded-lg text-yellow-400 text-sm">
                      <AlertCircle className="w-4 h-4 inline mr-2" />
                      Rezervasiyanız admin tərəfindən yoxlanılır. Təsdiqlədikdə sizə bildiriş göndəriləcək.
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </section>

        {/* Past Reservations */}
        {pastReservations.length > 0 && (
          <section>
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-gray-500" />
              Keçmiş Rezervasiyalar
            </h2>
            
            <div className="space-y-3">
              {pastReservations.map((reservation, index) => (
                <motion.div
                  key={reservation.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.03 }}
                  className="bg-[#0F0F0F] border border-white/5 rounded-lg p-4 opacity-60"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="text-center text-gray-500 text-sm">
                        <div className="font-bold">{reservation.date.split('-')[2]}</div>
                        <div className="text-xs">
                          {new Date(reservation.date).toLocaleDateString('az-AZ', { month: 'short' })}
                        </div>
                      </div>
                      <div>
                        <div className="text-white font-medium">{reservation.gameName}</div>
                        <div className="text-gray-500 text-sm">
                          {reservation.branchName} • {reservation.time}
                        </div>
                      </div>
                    </div>
                    {getStatusBadge(reservation.status)}
                  </div>
                </motion.div>
              ))}
            </div>
          </section>
        )}
      </div>

      <Footer />
    </div>
  );
}
